import { useNavigate, useSearchParams, Navigate } from "react-router-dom";
import { ArrowLeft, Printer, Award } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useStore } from "@/store/AppStore";
import { ranking } from "@/lib/selectors";
import { yearRanking, yearClosed } from "@/lib/highlights";
import { SEALS } from "@/lib/quality";
import { PROFILES, MONTH_LABEL, TODAY } from "@/data/mock";

const longDate = (d) => new Date(`${d}T12:00:00`).toLocaleDateString("pt-BR", { day: "numeric", month: "long", year: "numeric" });

function buildCert(state, p) {
  const company = state.companies.find((c) => c.id === p.get("empresa"));
  if (!company) return null;
  const tipo = p.get("tipo");
  if (!["mes", "ano", "selo"].includes(tipo)) return null;
  if (tipo === "selo") {
    const seal = state.seals[company.id];
    if (!seal) return null;
    return { title: SEALS[seal.level].label, eyebrow: "Certificado de qualidade", name: company.name, badge: seal.level.toUpperCase(), company,
      text: `reconhecida pela Empreendi Bellas por sua excelência em gestão, cumprimento de metas, processos, rotinas e manutenção, estando apta a receber e trocar indicações na Rede Bellas.`, date: seal.approvedAt };
  }
  const year = Number(p.get("ano"));
  const rows = tipo === "ano" ? (yearClosed(year) ? yearRanking(state, company.id, year) : []) : ranking(state, company.id);
  const r = rows.find((x) => x.id === p.get("colab"));
  if (!r || r.position > 3) return null;
  const period = tipo === "ano" ? `do ano de ${year}` : `de ${MONTH_LABEL}`;
  return { title: tipo === "ano" ? "Destaque do Ano" : "Destaque do Mês", eyebrow: "Certificado de reconhecimento", name: r.name, badge: `${r.position}º lugar`, company,
    text: `pela conquista do ${r.position}º lugar no ranking ${period} da ${company.name}, com ${r.score} pontos, em reconhecimento ao seu comprometimento, dedicação e excelência no trabalho.`, date: tipo === "ano" ? `${year}-12-31` : TODAY };
}

const Sign = ({ name, role }) => (
  <div className="flex w-full max-w-[16rem] flex-col items-center text-center">
    <p className="flex h-8 items-end text-lg text-neutral-800 sm:h-10 sm:text-2xl" style={{ fontFamily: "'Playfair Display', serif", fontStyle: "italic" }}>{name}</p>
    <div className="mt-1 w-full whitespace-nowrap border-t border-neutral-400 pt-2 text-[8px] uppercase tracking-[0.12em] text-neutral-500 sm:text-[10px]">{role}</div>
  </div>
);

export default function CertificatePage() {
  const { state, user } = useStore();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  if (user === false) return <Navigate to="/" replace />;
  if (!state) return null;
  const c = user.role === "colaboradora" ? null : buildCert(state, params);
  return (
    <div className="min-h-screen bg-neutral-100 px-4 py-8 print:bg-white print:p-0" data-testid="certificate-page">
      <style>{"@page { size: A4 landscape; margin: 0 } @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact } }"}</style>
      <div className="mx-auto mb-6 flex max-w-5xl justify-between print:hidden">
        <Button variant="outline" onClick={() => navigate(-1)} data-testid="certificate-back"><ArrowLeft className="mr-1 h-4 w-4" /> Voltar</Button>
        {c && <Button className="font-semibold" onClick={() => window.print()} data-testid="certificate-print"><Printer className="mr-1.5 h-4 w-4" /> Imprimir / salvar PDF</Button>}
      </div>
      {!c ? <p className="text-center text-sm text-neutral-500" data-testid="certificate-unavailable">Certificado não disponível.</p> : (
        <div data-testid="certificate" className="mx-auto w-full max-w-5xl bg-white p-3 shadow-xl sm:aspect-[297/210] sm:p-5 print:h-[210mm] print:w-[297mm] print:max-w-none print:shadow-none">
          <div className="flex h-full flex-col items-center justify-between border-2 border-gold px-6 py-8 text-center outline outline-1 outline-offset-4 outline-gold/50 sm:px-14 sm:py-10">
            <div className="flex flex-col items-center">
              <img src="/img/logo.jpg" alt="Empreendi Bellas" className="h-14 w-14 rounded-lg object-cover sm:h-20 sm:w-20" />
              <p className="mt-4 text-[10px] font-semibold uppercase tracking-[0.4em] text-gold-dark sm:text-xs">{c.eyebrow}</p>
              <h1 className="mt-2 font-display text-3xl font-semibold text-neutral-900 sm:text-5xl" data-testid="certificate-title">{c.title}</h1>
            </div>
            <div className="my-6 flex flex-col items-center">
              <p className="text-xs uppercase tracking-[0.25em] text-neutral-500">Conferido a</p>
              <p className="mt-2 font-display text-2xl font-semibold text-gold-dark sm:text-4xl" data-testid="certificate-name">{c.name}</p>
              <span className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-neutral-900 px-4 py-1 text-xs font-semibold text-gold" data-testid="certificate-badge"><Award className="h-3.5 w-3.5" /> {c.badge}</span>
              <p className="mt-4 max-w-2xl text-sm leading-relaxed text-neutral-600 sm:text-base">{c.text}</p>
            </div>
            <div className="w-full">
              <div className="grid grid-cols-2 items-start justify-items-center gap-6">
                <Sign name={c.company.owner} role={`Empresária · ${c.company.name}`} />
                <Sign name="Vanessa Nascimento" role="Consultora Master · Empreendi Bellas" />
              </div>
              <p className="mt-5 text-[10px] text-neutral-400 sm:text-xs">{c.company.city} · {longDate(c.date)}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
